import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as uuid from 'uuid';
import { CompanyExperiment } from '@blisspointmedia/bpm-types/dist/FeatureExperiments';
import { tokenDiffRenderer } from '@src/Components/PendingChanges/pending-changes-utils';
import { ExperimentTable } from './ExperimentTable';
import { GroupedDataRow } from './types';
import {
  EMPTY_CELL_TEXT,
  formatExperimentsDate,
  MAX_OVERLAY_LENGTH,
  MAX_SHOWN_GROUPED_COMPANY_NAMES
} from './utils';
import './ExperimentTable.scss';

const companyColumnRenderer = (data: GroupedDataRow<CompanyExperiment, 'company'>) => {
  if (!data.company) {
    return <div className='experiment-cell-text'>{EMPTY_CELL_TEXT}</div>;
  }

  if (data.company.length <= MAX_SHOWN_GROUPED_COMPANY_NAMES) {
    return <div className='experiment-cell-text'>{data.company.join(', ')}</div>;
  }

  const sortedCompanies = [...data.company].sort();
  const shownCompanies = sortedCompanies.slice(0, MAX_SHOWN_GROUPED_COMPANY_NAMES);
  const remainingCount = sortedCompanies.length - shownCompanies.length;

  const overlayShownCompanies = sortedCompanies.slice(0, MAX_OVERLAY_LENGTH);
  const overlayRemainingCount = sortedCompanies.length - overlayShownCompanies.length;

  return (
    <OverlayTrigger
      placement='right'
      overlay={
        <Tooltip id={uuid.v4()} className='experiment-overlay-trigger'>
          <div className='tooltip-text-container'>
            {overlayShownCompanies.map(company => (
              <div key={company}>{company}</div>
            ))}
            {overlayRemainingCount > 0 && (
              <div className='tooltip-item'>{`+ ${overlayRemainingCount} more`}</div>
            )}
          </div>
        </Tooltip>
      }
    >
      <div className='experiment-cell-text'>
        {`${shownCompanies.join(', ')}${remainingCount > 0 ? ` + ${remainingCount} more` : ''}`}
      </div>
    </OverlayTrigger>
  );
};

const headers = [
  {
    label: 'Experiment Flag',
    field: 'experiment_flag',
    type: 'select',
    options: 'experiment_flag',
    flex: 1,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (experimentFlag: string) => !experimentFlag || experimentFlag.length === 0
  },
  {
    label: 'Companies with Access',
    field: 'company',
    type: 'select',
    options: 'company',
    isMulti: true,
    flex: 1,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (company: string) => !company || company.length === 0,
    renderer: companyColumnRenderer,
    diffRenderer: tokenDiffRenderer<GroupedDataRow<CompanyExperiment, 'company'>>('company')
  },
  {
    label: 'Last Modified',
    field: 'lastmodified',
    type: 'text',
    flex: 1,
    modalRow: 0,
    modalFlex: 1,
    renderer: (data: CompanyExperiment) =>
      data.lastmodified ? (
        <div>{formatExperimentsDate(data.lastmodified)}</div>
      ) : (
        <div>{EMPTY_CELL_TEXT}</div>
      ),
    uneditable: true
  },
  {
    label: 'Modified By',
    field: 'lastuser',
    type: 'text',
    flex: 1,
    modalRow: 0,
    modalFlex: 1,
    uneditable: true
  }
];

const COMPANY_EXPERIMENTS_EXPECTED_KEYS: (keyof CompanyExperiment & string)[] = [
  'experiment_flag',
  'company',
  'created',
  'lastmodified'
];

const createNewCompanyExperiment = (
  groupByColumn: string,
  groupedColumn: string
): CompanyExperiment => {
  const now = new Date().toISOString();
  return {
    experiment_flag: groupByColumn,
    company: groupedColumn,
    created: now,
    lastmodified: now
  };
};

export const CompanyExperiments = () => (
  <ExperimentTable<CompanyExperiment>
    options={{
      headers,
      name: 'Company Experiments',
      addButtonText: 'Add Experiment',
      createNewRow: createNewCompanyExperiment,
      groupByColumn: 'experiment_flag',
      groupedColumn: 'company',
      expectedKeys: COMPANY_EXPERIMENTS_EXPECTED_KEYS,
      jsonField: 'company_experiments',
      modalTitle: 'New Company Experiment'
    }}
  />
);
